import React, { useState, useRef } from "react";
import { useRecorder } from "../hooks/useRecorder";
import { getUploadUrl, uploadAudio, startTranscription } from "../services/api";
import { RecordingMetadata, UserRole } from "../types/sop";

const PROCESS_AREAS = ["stow", "pick", "pack", "stage", "dispatch", "sort", "induct", "other"];

export function RecordPage() {
  const { isRecording, duration, start, stop, error: micError } = useRecorder();
  const [stationId, setStationId] = useState("");
  const [role, setRole] = useState<UserRole>("associate");
  const [processArea, setProcessArea] = useState("stow");
  const [submittedBy, setSubmittedBy] = useState("");
  const [status, setStatus] = useState<"idle" | "recording" | "uploading" | "done" | "error">("idle");
  const [jobId, setJobId] = useState("");
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const submit = async (blob: Blob, ext: string) => {
    setStatus("uploading");
    try {
      const meta: RecordingMetadata = { station_id: stationId, role, process_area: processArea, ...(submittedBy ? { submitted_by: submittedBy } : {}) };
      const { upload_url, audio_key } = await getUploadUrl({ ...meta, file_ext: ext } as any);
      await uploadAudio(upload_url, blob);
      const result = await startTranscription(audio_key, meta);
      setJobId(result.job_id);
      setStatus("done");
    } catch (e: any) {
      setError(e.message);
      setStatus("error");
    }
  };

  const handleRecord = async () => {
    if (isRecording) {
      const blob = await stop();
      await submit(blob, "webm");
    } else {
      await start();
      setStatus("recording");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const ext = file.name.split(".").pop() ?? "wav";
    await submit(file, ext);
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: 24 }}>
      <h2>Record Best Practice</h2>

      <label>Station ID</label>
      <input value={stationId} onChange={(e) => setStationId(e.target.value)} placeholder="e.g. DSD4"
        style={{ display: "block", width: "100%", padding: 8, marginBottom: 12 }} />

      <label>Name or Badge ID <span style={{ color: "#888", fontWeight: "normal" }}>(optional)</span></label>
      <input value={submittedBy} onChange={(e) => setSubmittedBy(e.target.value)} placeholder="e.g. Jane D. or badge #1234"
        style={{ display: "block", width: "100%", padding: 8, marginBottom: 12 }} />

      <label>Your Role</label>
      <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}
        style={{ display: "block", width: "100%", padding: 8, marginBottom: 12 }}>
        <option value="associate">Associate</option>
        <option value="pa">PA</option>
        <option value="manager">Manager</option>
      </select>

      <label>Process Area</label>
      <select value={processArea} onChange={(e) => setProcessArea(e.target.value)}
        style={{ display: "block", width: "100%", padding: 8, marginBottom: 24 }}>
        {PROCESS_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>

      {micError && <p style={{ color: "red" }}>{micError}</p>}

      <button onClick={handleRecord} disabled={!stationId || status === "uploading"}
        style={{
          width: "100%", padding: 16, fontSize: 18, cursor: "pointer",
          background: isRecording ? "#d32f2f" : "#1976d2", color: "#fff", border: "none", borderRadius: 8,
        }}>
        {isRecording ? `⏹ Stop Recording (${formatTime(duration)})` :
         status === "uploading" ? "Uploading..." : "🎙 Start Recording"}
      </button>

      <div style={{ textAlign: "center", margin: "16px 0", color: "#888" }}>— or —</div>

      <input ref={fileRef} type="file" accept="audio/*" onChange={handleFileUpload} hidden />
      <button onClick={() => fileRef.current?.click()}
        disabled={!stationId || status === "uploading"}
        style={{
          width: "100%", padding: 16, fontSize: 18, cursor: "pointer",
          background: "#fff", color: "#1976d2", border: "2px solid #1976d2", borderRadius: 8,
        }}>
        📁 Upload Audio File
      </button>
      {fileName && <p style={{ textAlign: "center", marginTop: 8, color: "#666" }}>{fileName}</p>}

      {status === "done" && (
        <div style={{ marginTop: 24, padding: 16, background: "#e8f5e9", borderRadius: 8 }}>
          <p>✅ Submitted! Your SOP is being generated.</p>
          <p>Job ID: <code>{jobId}</code></p>
          <p>Check the <a href="/review">Review Dashboard</a> in a few minutes.</p>
        </div>
      )}
      {status === "error" && <p style={{ color: "red", marginTop: 12 }}>{error}</p>}
    </div>
  );
}
